import { DEFAULT_LOCALE, LOCALES, type Locale } from "@confluence/shared";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiFailure } from "../api";
import { MESSAGES, type MessageKey } from "./messages";

const STORAGE_KEY = "confluence-locale";

function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (LOCALES as readonly string[]).includes(saved)) return saved as Locale;
  } catch {
    // stockage indisponible (navigation privée…) : on continue
  }
  for (const lang of navigator.languages ?? [navigator.language]) {
    const short = lang.slice(0, 2).toLowerCase();
    if ((LOCALES as readonly string[]).includes(short)) return short as Locale;
  }
  return DEFAULT_LOCALE;
}

export type Translate = (key: MessageKey, params?: Record<string, string>) => string;

interface I18n {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Translate;
  /** Message lisible pour une erreur d'API, dans la langue courante. */
  errorText: (err: unknown) => string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // ignoré
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18n>(() => {
    const dict = MESSAGES[locale];
    const t: Translate = (key, params) => {
      let s: string = dict[key] ?? MESSAGES.en[key] ?? key;
      if (params) for (const [k, v] of Object.entries(params)) s = s.replaceAll(`{${k}}`, v);
      return s;
    };
    const errorText = (err: unknown) => {
      if (err instanceof ApiFailure) {
        const key = `error.${err.code}` as MessageKey;
        if (!(key in dict)) return err.message;
        // Refus d'un service externe : son message aide à corriger (clé, expéditeur, adresse IP…).
        const detail = err.code === "MAIL_FAILED" ? err.message.replace(/^The email could not be sent: /, "") : "";
        return detail ? `${t(key)} (${detail})` : t(key);
      }
      return t("error.INTERNAL");
    };
    return { locale, setLocale, t, errorText };
  }, [locale, setLocale]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useI18n outside I18nProvider");
  return ctx;
}

export const LANGUAGE_NAMES: Record<Locale, string> = Object.fromEntries(
  LOCALES.map((l) => [l, MESSAGES[l]["lang.name"]]),
) as Record<Locale, string>;
